import { BehaviorSubject, defer, delay, Observable, of } from 'rxjs';
import { distinctUntilChanged, finalize, map, tap } from 'rxjs/operators';

export interface IPage<T> {
  currentPage: number;
  data: T[];
  lastPage: number;
  perPage: number;
  total?: number;
}

/**
 * Server-side paged list with a per-page cache — what EMMET actually used Akita's
 * `PaginatorPlugin` for. Deliberately not a state-management library: no
 * normalized entity store, because the plugin's store was write-only here (it
 * upserted a page then read it straight back by id, and every template binds the
 * `getPage()` result rather than `selectAll()`). See STEP-4D-PLAN.md.
 *
 * Compose it in a component via its existing `InjectionToken` factory.
 */
export class PagedCollection<T> {
  private readonly page$: BehaviorSubject<number>;
  private readonly loadingState$ = new BehaviorSubject<boolean>(false);
  private pages = new Map<number, IPage<T>>();

  /**
   * `delay(0)` carried over from Akita's `isLoading$`. Components set loading
   * synchronously while subscribing to `selectedEquipmentId$` (a BehaviorSubject),
   * inside the change-detection cycle that first reads the bound value, so
   * emitting on the same tick trips NG0100.
   */
  public readonly loading$ = this.loadingState$.pipe(distinctUntilChanged(), delay(0));

  public constructor(startWith = 1) {
    this.page$ = new BehaviorSubject(startWith);
  }

  public get currentPage(): number {
    return this.page$.value;
  }

  public get pageChanges(): Observable<number> {
    return this.page$.asObservable();
  }

  /** Re-requesting the current page is a no-op unless it fell out of the cache. */
  public setPage(page: number): void {
    if (page !== this.currentPage || !this.pages.has(page)) {
      this.page$.next(page);
    }
  }

  public setLoading(loading: boolean): void {
    this.loadingState$.next(loading);
  }

  /**
   * Akita guarded this with an `initial` flag, because its `clearCache()` also
   * wiped the *shared* entity store that other consumers read from. This cache is
   * private and every caller clears it upstream of `getPage()` in the same stream,
   * so clearing an empty cache cannot change an outcome.
   */
  public clearCache(): void {
    this.pages = new Map();
  }

  /**
   * Serves the current page from cache, otherwise runs `request` and caches it.
   *
   * Ends the load when the page arrives and on every exit path — error,
   * unsubscribe, empty completion, and cache hit (components call
   * `setLoading(true)` themselves before the stream starts, to avoid a "no data"
   * flash). Some requests stay subscribed to selection state after returning a page,
   * so `finalize` alone cannot represent success.
   *
   * Nothing is cached unless a page actually arrives, so a failed load can be
   * retried. Domain services report failures on their own `error$` and return
   * `EMPTY`, which completes without emitting — leaving the table's previous rows
   * in place while the error renders, and letting `finalize` clear the spinner.
   */
  public getPage(request: () => Observable<IPage<T>>): Observable<IPage<T>> {
    return defer(() => {
      const cached = this.pages.get(this.currentPage);

      if (cached) {
        this.setLoading(false);

        return of(cached);
      }

      this.setLoading(true);

      return request().pipe(
        map((page) => ({ ...page, total: this.resolveTotal(page) })),
        tap((page) => {
          this.pages.set(page.currentPage, page);
          this.setLoading(false);
        }),
        finalize(() => {
          this.setLoading(false);
        })
      );
    });
  }

  /**
   * Only pm-operation's service reports `total`; the rest omit it and relied on
   * Akita deriving it, which every table needs for `[nzTotal]`.
   */
  private resolveTotal({ total, lastPage, perPage, data }: IPage<T>): number {
    if (total !== undefined && !isNaN(total)) {
      return total;
    }

    return lastPage === 1 ? data.length : perPage * lastPage;
  }
}
